import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import LessonGrid from '@anu/components/LessonGrid';
import QuizSubmit from '@anu/components/QuizSubmit';
import paragraphMappings from '@anu/utilities/paragraphMappings';
import Box from '@material-ui/core/Box';
import QuizAlert from '@anu/components/QuizAlert';
import Link from '@material-ui/core/Link';

// TODO - should be a pure function component with hooks.
class ParagraphsWithQuiz extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      assessmentData: {},
      correctValues: null,
      correctValuesCount: !isNaN(props.correctValuesCount) ? props.correctValuesCount : -1,
      isSubmitting: false,
      openDialog: false,
      readyToSubmit: !props.isSingleSubmission,
      isSingleSubmission: props.isSingleSubmission,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSubmissionConfirmation = this.handleSubmissionConfirmation.bind(this);
    this.checkSubmission = this.checkSubmission.bind(this);
  }

  /**
   * Capture form widget value change for each answer.
   */
  handleChange(value, paragraphId) {
    this.setState((prevState) => ({
      assessmentData: {
        ...prevState.assessmentData,
        [paragraphId]: value,
      },
    }));
  }

  handleSubmissionConfirmation(value) {
    this.setState(
      () => ({
        readyToSubmit: value,
        openDialog: false,
      }),
      this.handleSubmit
    );
  }

  checkSubmission() {
    if (this.state.isSingleSubmission) {
      this.setState({
        openDialog: true,
      });
    } else {
      this.handleSubmit();
    }
  }

  async buttonAction() {
    if (this.state.correctValuesCount * (100 / Object.keys(this.state.assessmentData).length) >= 80) {
      window.open("/certification", "_blank");
    } else {
      location.reload();
    }
  }

  async handleSubmit() {
    if (this.state.isSingleSubmission && !this.state.readyToSubmit) {
      return;
    }

    const { nodeId } = this.props;
    const { assessmentData } = this.state;

    this.setState({
      isSubmitting: true,
      correctValues: null,
    });

    try {
      const token = await fetch(Drupal.url('session/token'));
      const response = await fetch(Drupal.url('assessments/assessment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': await token.text(),
        },
        body: JSON.stringify({
          nid: nodeId,
          data: assessmentData,
        }),
      });

      if (response.ok) {
        const payload = await response.json();

        this.setState({
          correctValues: payload.correctAnswers || null,
          correctValuesCount: payload.correctAnswersCount,
        });
        this.props.submitQuiz(true);
      } else {
        alert(Drupal.t('Quiz submission failed. Please try again.', {}, { context: 'ANU LMS' }));
        console.error(response.status, await response.text());
      }
    } catch (error) {
      console.error(error);
    }

    this.setState({ isSubmitting: false });
  }

  render() {
    const { items, isSingleSubmission, isSubmitted, prevLesson } = this.props;
    const {
      assessmentData,
      correctValues,
      correctValuesCount,
      isSubmitting,
      openDialog,
    } = this.state;

    const canSubmit = !isSubmitted;

    const paragraphs = items.map((paragraph) => {
      if (paragraph.bundle in paragraphMappings) {
        const Component = paragraphMappings[paragraph.bundle];

        // Non-quiz paragraph - just render it.
        if (!paragraph.bundle.startsWith('question_')) {
          return (
            <Box mt={4} mb={4} key={paragraph.id}>
              <Component key={paragraph.id} {...paragraph} />
            </Box>
          );
        }

        // Quiz paragraph receives some extra props to handle widget's behavior.
        const value = paragraph.aqid in assessmentData ? assessmentData[paragraph.aqid] : null;
        const correctValue =
          correctValues && paragraph.aqid in correctValues ? correctValues[paragraph.aqid] : null;

        return (
          <Box mt={4} mb={4} key={paragraph.id}>
            <Component
              key={paragraph.id}
              {...paragraph}
              value={value}
              onChange={(value) => this.handleChange(value, paragraph.aqid)}
              correctQuizValue={correctValue}
              isSubmitting={isSubmitting}
              isSubmitted={isSubmitted}
              canSubmit={canSubmit}
              prevLesson={prevLesson}
              isQuiz
            />
          </Box>
        );
      }
      return null;
    });
    
    
    // Hide the correct answers
    if (isSubmitted) {
  
      const answers = document.querySelectorAll('[data-test="anu-lms-radio-correct-error"]');

      if (answers.length > 0) {
        answers.forEach((answer) => {
          answer.querySelector('.MuiSvgIcon-root').style.color = "rgba(0, 0, 0, 0.26)";
          answer.querySelector('.MuiSvgIcon-root.jss44').style.display = "none";
        });  
      }
    }

    return (
      <>
        <LessonGrid>
          {isSubmitted && correctValuesCount !== -1 && (
            <>
            <Typography variant="h4" className='grade-title'>
              {Drupal.t(
                '@status!',
                {
                  '@status': correctValuesCount * (100 / Object.keys(assessmentData).length) >= 80 ? 'Passed' : 'Failed',
                },
                { context: 'ANU LMS' }
              )}
            </Typography>
            <Typography variant="h5" className='grade-subtitle'>
                {Drupal.t(
                  'You scored @score%',
                  {
                    '@score': Math.round(correctValuesCount * (100 / Object.keys(assessmentData).length)),
                    '@amount': correctValuesCount,
                    '@total': Object.keys(assessmentData).length,
                  },
                  { context: 'ANU LMS' }
                )}
            </Typography>
            <Button variant="contained" disableElevation onClick={(e) => { this.buttonAction() }} className='grade-cert'>
              {Drupal.t('Please click here to @message.',
                {
                  '@message': correctValuesCount * (100 / Object.keys(assessmentData).length) >= 80 ? 'download your certificate' : 'retake the quiz',
                },
                { context: 'ANU LMS' }
              )}
            </Button>
            <Typography variant="body1" className='grade-text'>
                {Drupal.t('Please review the following resources as they may help you prepare for this course:')}
            </Typography>
            <Link href="https://www.energy.gov/eere/femp/performance-contracting-national-resource-center" className="grade-link" target="_blank"> Performance Contracting National Resource Center</Link>
            <Link href="https://www.energyservicescoalition.org/performance-contracting" className="grade-link" target="_blank">What is Energy Performance Contracting?</Link>
            </>
          )}
            

          {isSingleSubmission && (
            <QuizAlert open={openDialog} handleClose={this.handleSubmissionConfirmation} />
          )}
        </LessonGrid>

        {paragraphs}

        {canSubmit && (
          <QuizSubmit
            onSubmit={this.checkSubmission}
            prevLesson={prevLesson}
            isSubmitting={isSubmitting}
            isQuiz
          />
        )}
      </>
    );
  }
}

ParagraphsWithQuiz.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape),
  nodeId: PropTypes.number,
  correctValuesCount: PropTypes.number,
  isSingleSubmission: PropTypes.bool,
  submitQuiz: PropTypes.func,
  isSubmitted: PropTypes.bool,
  prevLesson: PropTypes.shape(),
};

export default ParagraphsWithQuiz;
